/**
 * Supplier Campaign Builder - Product Availability Alert System
 * Allows suppliers to proactively push inventory alerts to hospitals
 * 
 * Features:
 * - Alert Type: "Backorder Mitigation" or "Promotional Stock"
 * - Product template with B. Braun styling
 * - Broadcasts to ALL hospitals with "Marketplace Alerts" enabled
 */

import { useState } from "react";
import { toast } from "sonner";
import {
  Megaphone,
  Package,
  DollarSign,
  Clock,
  Upload,
  Image as ImageIcon,
  Send,
  Loader2,
  AlertTriangle,
  Tag,
  X,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupplierCampaignBuilder({ 
  open, 
  onClose, 
  supplierId, 
  supplierName,
  authToken,
  onSuccess 
}) {
  const [alertType, setAlertType] = useState("backorder_mitigation");
  const [submitting, setSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    manufacturer: "",
    manufacturer_number: "",
    product_name: "",
    description: "",
    quantity_available: 1,
    unit_price: 0,
    lead_time_days: 3,
    expiration_date: "",
    notes: ""
  });

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit campaign/alert
  const handleSubmit = async () => {
    if (!formData.product_name || !formData.quantity_available || !formData.unit_price) {
      toast.error("Please fill in product name, quantity, and price");
      return;
    }
    
    setSubmitting(true);
    try {
      // First upload image if present
      let imageUrl = null;
      if (imageFile) {
        const formDataImg = new FormData();
        formDataImg.append("file", imageFile);
        
        const uploadRes = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
          body: formDataImg
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          imageUrl = uploadData.url;
        }
      }
      
      // Submit campaign
      const res = await fetch(`${API_URL}/api/suppliers/campaigns`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          alert_type: alertType,
          ...formData,
          product_image_url: imageUrl,
          supplier_id: supplierId,
          supplier_name: supplierName
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to create campaign");
      }
      
      toast.success(`Campaign created! Alert sent to ${data.hospitals_notified || 0} hospitals.`);
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        manufacturer: "",
        manufacturer_number: "",
        product_name: "",
        description: "",
        quantity_available: 1,
        unit_price: 0,
        lead_time_days: 3,
        expiration_date: "",
        notes: ""
      });
      setImageFile(null);
      setImagePreview(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-600" />
            Create Product Availability Alert
          </DialogTitle>
          <DialogDescription>
            Broadcast your available inventory to hospitals across the network
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-4">
            {/* Alert Type Toggle */}
            <div>
              <Label>Alert Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setAlertType("backorder_mitigation")}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    alertType === "backorder_mitigation"
                      ? "border-amber-500 bg-amber-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <AlertTriangle className={`w-5 h-5 mb-1 ${
                    alertType === "backorder_mitigation" ? "text-amber-600" : "text-slate-400"
                  }`} />
                  <p className="font-medium text-sm">Backorder Mitigation</p>
                  <p className="text-xs text-slate-500">Fill shortage demand</p>
                </button>
                <button
                  type="button"
                  onClick={() => setAlertType("promotional_stock")}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    alertType === "promotional_stock"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Tag className={`w-5 h-5 mb-1 ${
                    alertType === "promotional_stock" ? "text-emerald-600" : "text-slate-400"
                  }`} />
                  <p className="font-medium text-sm">Promotional Stock</p>
                  <p className="text-xs text-slate-500">Special offer pricing</p>
                </button>
              </div>
            </div>

            {/* Manufacturer */}
            <div>
              <Label>Manufacturer</Label>
              <Input
                value={formData.manufacturer}
                onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                placeholder="e.g., B. Braun Medical"
              />
            </div>

            {/* Manufacturer Number */}
            <div>
              <Label>Manufacturer Part # (MFR#)</Label>
              <Input
                value={formData.manufacturer_number}
                onChange={(e) => setFormData({...formData, manufacturer_number: e.target.value})}
                placeholder="e.g., R5001-01"
              />
            </div>

            {/* Product Name */}
            <div>
              <Label>Product Name *</Label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                placeholder="e.g., Sterile Water for Irrigation"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Product specifications, size, packaging..."
                rows={2}
              />
            </div>

            {/* Quantity & Price Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity Available *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity_available}
                  onChange={(e) => setFormData({...formData, quantity_available: parseInt(e.target.value) || 1})}
                />
              </div>
              <div>
                <Label>Unit Price ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({...formData, unit_price: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            {/* Lead Time */}
            <div>
              <Label>Lead Time (Days)</Label>
              <Input
                type="number"
                min="1"
                value={formData.lead_time_days}
                onChange={(e) => setFormData({...formData, lead_time_days: parseInt(e.target.value) || 3})}
              />
            </div>

            {/* Product Image Upload */}
            <div>
              <Label>Product Image</Label>
              <div className="mt-2">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Product preview" 
                      className="w-full h-32 object-contain rounded-lg border border-slate-200 bg-slate-50"
                    />
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Click to upload image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-4">
            <Label>Email Template Preview</Label>
            
            {/* B. Braun Style Template Preview */}
            <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              {/* Header */}
              <div className="bg-slate-600 text-white px-4 py-3">
                <p className="text-xs opacity-80">Product Availability Alert</p>
                <p className="font-semibold">
                  {formData.manufacturer || "Manufacturer"}
                </p>
              </div>
              
              {/* Yellow Accent Bar */}
              <div className={`h-1 ${
                alertType === "backorder_mitigation" ? "bg-amber-400" : "bg-emerald-400"
              }`} />
              
              {/* Content */}
              <div className="p-4 bg-white">
                {/* Product Image */}
                <div className="w-full h-24 bg-slate-100 rounded mb-3 flex items-center justify-center overflow-hidden">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Product" className="h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                
                {/* Product Info Table */}
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 text-slate-500">MFR#</td>
                      <td className="py-2 font-medium text-right">
                        {formData.manufacturer_number || "—"}
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 text-slate-500">Description</td>
                      <td className="py-2 font-medium text-right">
                        {formData.product_name || "Product Name"}
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 text-slate-500">QTY</td>
                      <td className="py-2 font-medium text-right">
                        {formData.quantity_available}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 text-slate-500">Lead Time</td>
                      <td className="py-2 font-medium text-right">
                        {formData.lead_time_days} days
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                {/* Price */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-slate-500 uppercase">Price</p>
                  <p className="text-2xl font-bold text-red-600">
                    ${formData.unit_price?.toFixed(2) || "0.00"}
                  </p>
                </div>
                
                {/* Alert Type Badge */}
                <div className="mt-4 text-center">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                    alertType === "backorder_mitigation" 
                      ? "bg-amber-100 text-amber-700" 
                      : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {alertType === "backorder_mitigation" ? (
                      <>
                        <AlertTriangle className="w-3 h-3" />
                        Backorder Mitigation
                      </>
                    ) : (
                      <>
                        <Tag className="w-3 h-3" />
                        Promotional Stock
                      </>
                    )}
                  </span>
                </div>
              </div>
              
              {/* Footer */}
              <div className="bg-slate-50 px-4 py-2 border-t border-slate-200">
                <p className="text-xs text-slate-500 text-center">
                  From: {supplierName || "Your Company"}
                </p>
              </div>
            </div>

            {/* Broadcast Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <Send className="w-4 h-4" />
                This alert will be sent to <strong>all hospitals</strong> with Marketplace Alerts enabled.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting || !formData.product_name || !formData.unit_price}
            className={alertType === "backorder_mitigation" 
              ? "bg-amber-600 hover:bg-amber-700" 
              : "bg-emerald-600 hover:bg-emerald-700"
            }
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Megaphone className="w-4 h-4 mr-2" />
            )}
            Broadcast Alert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
